import React, { useState, useEffect } from 'react';
import { fetchWithCorpId } from '../utils/api';
import { useParams } from 'react-router-dom';

function TimesheetForm({ mode = 'create', initialData = null, onSuccess, onCancel }) {
  const { id } = useParams();
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState({
    id: '',
    employeeId: '',
    vendorId: '',
    periodStartDate: '',
    periodEndDate: '',
    totalHours: '',
    status: 'Pending',
    billingCode: '',
    notes: ''
  });
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimesheet = async () => {
      if (isEdit && id) {
        try {
          const data = await fetchWithCorpId(`/timesheets/${id}`);
          setFormData({
            ...data,
            // Ensure dates are in the correct format
            periodStartDate: data.periodStartDate ? new Date(data.periodStartDate).toISOString().split('T')[0] : '',
            periodEndDate: data.periodEndDate ? new Date(data.periodEndDate).toISOString().split('T')[0] : '',
          });
        } catch (error) {
          console.error('Error fetching timesheet:', error);
          setError('Failed to load timesheet data');
        }
      }
    };
    fetchTimesheet();
    fetchData();
  }, [isEdit, id]);

  const fetchData = async () => {
    try {
      const [employeesData, vendorsData] = await Promise.all([
        fetchWithCorpId('/employees'),
        fetchWithCorpId('/vendors')
      ]);
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = mode === 'edit' ? `/timesheets/${formData.id}` : '/timesheets';
      const method = mode === 'edit' ? 'PUT' : 'POST';
      
      await fetchWithCorpId(url, {
        method,
        body: JSON.stringify(formData)
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      alert('Failed to save timesheet');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-section">
        <label>Timesheet ID:
          <input 
            type="text" 
            name="id" 
            value={formData.id} 
            readOnly
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </label>
      </div>

      <div className="form-section">
        <label>Employee:
          <select name="employeeId" value={formData.employeeId} onChange={handleChange} required>
            <option value="">Select Employee</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </label>

        <label>Vendor:
          <select name="vendorId" value={formData.vendorId} onChange={handleChange} required>
            <option value="">Select Vendor</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
      </div>

      <div className="form-section">
        <label>Period Start Date:
          <input 
            type="date" 
            name="periodStartDate" 
            value={formData.periodStartDate} 
            onChange={handleChange}
            required
          />
        </label>

        <label>Period End Date:
          <input 
            type="date" 
            name="periodEndDate" 
            value={formData.periodEndDate} 
            onChange={handleChange}
            required
          />
        </label>

        <label>Total Hours:
          <input 
            type="number" 
            step="0.01" 
            name="totalHours" 
            value={formData.totalHours} 
            onChange={handleChange}
            required
          />
        </label>

        <label>Status:
          <select name="status" value={formData.status} onChange={handleChange} required>
            <option value="Pending">Pending</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Locked">Locked</option>
          </select>
        </label>

        <label>Billing Code:
          <input 
            type="text" 
            name="billingCode" 
            value={formData.billingCode} 
            onChange={handleChange}
            required
          />
        </label>
      </div>

      <div className="form-section">
        <label>Notes:
          <textarea 
            name="notes" 
            value={formData.notes} 
            onChange={handleChange}
            rows={4}
          />
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          type="submit" 
          style={{ 
            padding: '10px 20px',
            height: '40px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0
          }}
        >
          Save
        </button>
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              padding: '10px 20px',
              height: '40px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default TimesheetForm; 